import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { parseJsonRecord } from '~/models/filterCodec'
import {
  DEFAULT_DETAIL_URL_FALLBACK,
  type DetailBgSelection,
  type DetailToggleState,
  type DetailUrlFallback,
  hiddenTokensOf,
  normalizeBgSelection,
  togglesFromHiddenTokens,
} from '~/pages/BalanceDemandDetail/models/detailUrlState'

/**
 * 需要詳細の前回の選択 (AC-BDB-016 / AC-SC-031)。URL に指定が無い時の初期値に使う。
 *
 * BG・小売はエリア別に持つ。BG はエリアに属し、選択肢は (表示日, エリア) で
 * 決まるので、別エリアの BG を復元しても選択肢に無く「すべて」に戻るだけになる。
 * トグルは見せ方の好みでエリアに依らないので 1 つだけ持つ。
 *
 * 表示日は持たない (再訪時は当日、AC-BDB-016)。
 */
export const DETAIL_FILTERS_STORAGE_KEY =
  'ebalance-balance-demand-detail-filters'

export type StoredDetailFilters = {
  /** key は AreaCode の数値文字列。 */
  bgByArea: Readonly<Record<string, DetailBgSelection>>
  toggles: DetailToggleState
}

export const EMPTY_STORED_DETAIL_FILTERS: StoredDetailFilters = {
  bgByArea: {},
  toggles: {
    series: DEFAULT_DETAIL_URL_FALLBACK.series,
    voltageEnabled: DEFAULT_DETAIL_URL_FALLBACK.voltageEnabled,
    analysisEnabled: DEFAULT_DETAIL_URL_FALLBACK.analysisEnabled,
    detailEnabled: DEFAULT_DETAIL_URL_FALLBACK.detailEnabled,
  },
}

/** localStorage に載せる形。系列・親トグルは URL と同じ公開 token で書く。 */
type PersistedShape = {
  bgByArea: Record<string, DetailBgSelection>
  hiddenSeries: string[]
  hiddenGroups: string[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * localStorage の生文字列を復元する。壊れた値・未知の token・書式不正の BG / 小売は
 * 項目ごとに既定へ落とす (deploy skew・手書き改変で不正な request を送らない)。
 */
export function deserializeDetailFilters(
  raw: string | undefined,
): StoredDetailFilters {
  if (raw == null) return EMPTY_STORED_DETAIL_FILTERS
  const parsed = parseJsonRecord(raw)
  if (parsed === undefined) return EMPTY_STORED_DETAIL_FILTERS

  const bgByArea: Record<string, DetailBgSelection> = {}
  if (isRecord(parsed.bgByArea)) {
    for (const [area, entry] of Object.entries(parsed.bgByArea)) {
      if (!isRecord(entry)) continue
      bgByArea[area] = normalizeBgSelection(entry.bgCode, entry.retailerCode)
    }
  }

  return {
    bgByArea,
    toggles: togglesFromHiddenTokens(
      Array.isArray(parsed.hiddenSeries) ? parsed.hiddenSeries : [],
      Array.isArray(parsed.hiddenGroups) ? parsed.hiddenGroups : [],
    ),
  }
}

export function serializeDetailFilters(stored: StoredDetailFilters): string {
  const shape: PersistedShape = {
    bgByArea: { ...stored.bgByArea },
    ...hiddenTokensOf(stored.toggles),
  }
  return JSON.stringify(shape)
}

/** 保存値のうち、指定エリアを URL の補完に使う値。 */
export function fallbackForArea(
  stored: StoredDetailFilters,
  areaCode: AreaCode,
): DetailUrlFallback {
  const bg = stored.bgByArea[String(areaCode)]
  return {
    bgCode: bg?.bgCode ?? DEFAULT_DETAIL_URL_FALLBACK.bgCode,
    retailerCode: bg?.retailerCode ?? DEFAULT_DETAIL_URL_FALLBACK.retailerCode,
    ...stored.toggles,
  }
}

export function withAreaBgSelection(
  stored: StoredDetailFilters,
  areaCode: AreaCode,
  selection: DetailBgSelection,
): StoredDetailFilters {
  return {
    ...stored,
    bgByArea: {
      ...stored.bgByArea,
      [String(areaCode)]: normalizeBgSelection(
        selection.bgCode,
        selection.retailerCode,
      ),
    },
  }
}

export function withToggles(
  stored: StoredDetailFilters,
  toggles: DetailToggleState,
): StoredDetailFilters {
  return {
    ...stored,
    toggles: {
      series: toggles.series,
      voltageEnabled: toggles.voltageEnabled,
      analysisEnabled: toggles.analysisEnabled,
      detailEnabled: toggles.detailEnabled,
    },
  }
}
